"use client"
import Link from "next/link"
import Image from "next/image"
import { useState, useEffect, useRef, useCallback } from "react"
import { usePathname, useRouter } from "next/navigation";
import { Menu, X, ChevronDown, ChevronUp, Search, User, LogOut, ShoppingBag, Heart, ShoppingCart, Gift, Loader2 } from "lucide-react"
import { navLinks, customMadeServices } from "@/lib/data"

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://modishkreations.peoplesflow.in/api";

// ─── Types ─────────────────────────────────────────────────────────────────────
interface SearchProduct {
  id: number;
  name: string;
  slug: string;
  category: string;
  sale_price: number | null;
  regular_price: number;
  gender: string | null;
}

interface CartItem {
  quantity?: number;
}

interface ProfileResponse {
  success: boolean;
  data?: {
    first_name?: string;
    last_name?: string;
  };
}

interface CartResponse {
  success: boolean;
  data?: {
    items?: CartItem[];
  };
}

interface WishlistResponse {
  success: boolean;
  data?: unknown[] | { items?: unknown[] };
}

interface SearchApiResponse {
  success: boolean;
  data?: SearchProduct[];
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem("auth_token") || localStorage.getItem("auth_token") || null;
}

function authHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${getToken()}`,
    "Content-Type": "application/json",
  };
}

// ─── Debounce hook ──────────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Search Dropdown ────────────────────────────────────────────────────────────
function SearchDropdown({
  results, loading, query, onSelect, onViewAll,
}: {
  results: SearchProduct[];
  loading: boolean;
  query: string;
  onSelect: (p: SearchProduct) => void;
  onViewAll: () => void;
}) {
  if (!query || query.trim().length < 2) return null;

  return (
    <div className="absolute left-0 right-0 top-[calc(100%+6px)] bg-white border border-gray-100 rounded-2xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
      {/* Header */}
      <div className="px-4 py-2 border-b border-gray-50 flex items-center justify-between">
        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400">Results</span>
        {loading
          ? <Loader2 size={11} className="text-[#C7AB5F] animate-spin" />
          : <span className="text-[10px] font-bold text-[#C7AB5F]">{results.length} found</span>
        }
      </div>

      {/* Loading skeleton */}
      {loading && results.length === 0 && (
        <div className="px-4 py-3 space-y-2.5">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex justify-between items-center gap-3 animate-pulse">
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-gray-100 rounded-full w-3/4" />
                <div className="h-2 bg-gray-50 rounded-full w-1/3" />
              </div>
              <div className="h-3 bg-gray-100 rounded-full w-14" />
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {!loading && results.length === 0 && (
        <div className="px-4 py-5 text-center">
          <p className="text-[12px] text-gray-400">
            No results for <span className="font-semibold text-gray-600">&quot;{query}&quot;</span>
          </p>
          <p className="text-[10px] text-gray-300 mt-0.5">Try a different keyword</p>
        </div>
      )}

      {/* Results */}
      {results.map((product) => (
        <button
          key={product.id}
          type="button"
          onClick={() => onSelect(product)}
          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#faf7f2] transition-colors text-left border-b border-gray-50 last:border-b-0 group"
        >
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-[#051173] truncate group-hover:text-[#C7AB5F] transition-colors">
              {product.name}
            </p>
            <p className="text-[10px] text-[#C7AB5F] uppercase tracking-wider mt-0.5">
              {product.category}
              {product.gender && <span className="text-gray-300 ml-1">· {product.gender}</span>}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[12px] font-bold text-[#1a1a1a]">
              ₹{(product.sale_price ?? product.regular_price).toLocaleString()}
            </p>
            {product.sale_price && (
              <p className="text-[10px] text-gray-300 line-through">
                ₹{Number(product.regular_price).toLocaleString()}
              </p>
            )}
          </div>
        </button>
      ))}

      {/* View all */}
      {results.length > 0 && (
        <button
          type="button"
          onClick={onViewAll}
          className="w-full px-4 py-2.5 text-[11px] font-bold tracking-wider uppercase text-[#C7AB5F] hover:bg-[#fdf8f0] transition-colors text-center border-t border-gray-50"
        >
          View all results for &quot;{query}&quot; →
        </button>
      )}
    </div>
  );
}

// ─── Main Navbar ────────────────────────────────────────────────────────────────
export default function Navbar() {
  const [open, setOpen]                           = useState(false);
  const [mobileCustomOpen, setMobileCustomOpen]   = useState(false);
  const [mobileShopOpen, setMobileShopOpen]       = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen]   = useState(false);

  // ── Auth ───────────────────────────────────────────────────────────────────
  const [isLoggedIn, setIsLoggedIn]       = useState(false);
  const [userInitials, setUserInitials]   = useState("US");
  const [userName, setUserName]           = useState("");
  const [profileDrop, setProfileDrop]     = useState(false);
  const [cartCount, setCartCount]         = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  // ── Search ─────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]       = useState("");
  const [searchResults, setSearchResults]   = useState<SearchProduct[]>([]);
  const [searchLoading, setSearchLoading]   = useState(false);
  const [desktopFocused, setDesktopFocused] = useState(false);
  const [mobileFocused, setMobileFocused]   = useState(false);

  const debouncedQuery = useDebounce(searchQuery, 380);

  const profileMenuRef   = useRef<HTMLDivElement>(null);
  const desktopSearchRef = useRef<HTMLFormElement>(null);
  const mobileSearchRef  = useRef<HTMLFormElement>(null);

  const pathname = usePathname();
  const router   = useRouter();

  // ── Eagerly show spinner, then debounced fetch ─────────────────────────────
  useEffect(() => {
    if (searchQuery.trim().length >= 2) setSearchLoading(true);
    else { setSearchResults([]); setSearchLoading(false); }
  }, [searchQuery]);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!q || q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    fetch(`${API_BASE}/products?search=${encodeURIComponent(q)}&status=active&limit=6`)
      .then(r => r.json())
      .then((data: SearchApiResponse) => {
        if (data.success) setSearchResults(data.data ?? []);
        else setSearchResults([]);
      })
      .catch(() => setSearchResults([]))
      .finally(() => setSearchLoading(false));
  }, [debouncedQuery]);

  // ── Outside click handler ──────────────────────────────────────────────────
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node))
        setProfileDrop(false);
      if (desktopSearchRef.current && !desktopSearchRef.current.contains(e.target as Node))
        setDesktopFocused(false);
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target as Node))
        setMobileFocused(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // ── Reset on route change ──────────────────────────────────────────────────
  useEffect(() => {
    setOpen(false);
    setMobileSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  }, [pathname]);

  // ── Auth fetch ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsLoggedIn(false);
      setUserInitials("US");
      setUserName("");
      setCartCount(0);
      setWishlistCount(0);
      return;
    }
    setIsLoggedIn(true);

    Promise.all([
      fetch(`${API_BASE}/profile`,  { headers: authHeaders() }).then(r => r.json()).catch(() => null) as Promise<ProfileResponse | null>,
      fetch(`${API_BASE}/cart`,     { headers: authHeaders() }).then(r => r.json()).catch(() => null) as Promise<CartResponse | null>,
      fetch(`${API_BASE}/wishlist`, { headers: authHeaders() }).then(r => r.json()).catch(() => null) as Promise<WishlistResponse | null>,
    ]).then(([profile, cart, wishlist]) => {
      if (profile?.success && profile.data) {
        const { first_name, last_name } = profile.data;
        const fullName = `${first_name || ""} ${last_name || ""}`.trim();
        setUserName(fullName);
        const initials = [first_name?.[0], last_name?.[0]]
          .filter(Boolean).join("").toUpperCase()
          || first_name?.slice(0, 2).toUpperCase() || "US";
        setUserInitials(initials);
        const storage = sessionStorage.getItem("auth_token") ? sessionStorage : localStorage;
        storage.setItem("user_name", fullName);
      }
      if (cart?.success && cart.data) {
        const count = cart.data.items?.reduce(
          (sum: number, item: CartItem) => sum + (item.quantity ?? 1), 0) ?? 0;
        setCartCount(count);
      }
      if (wishlist?.success && wishlist.data) {
        const count = Array.isArray(wishlist.data)
          ? wishlist.data.length : (wishlist.data.items?.length ?? 0);
        setWishlistCount(count);
      }
    });
  }, [pathname]);

  // ── Search handlers ────────────────────────────────────────────────────────
  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    setDesktopFocused(false);
    setMobileFocused(false);
    setMobileSearchOpen(false);
  }, []);

  const handleSelect = useCallback((product: SearchProduct) => {
    const genderPath = product.gender?.toLowerCase() === "women" ? "women" : "men";
    router.push(`/shop/${genderPath}?product=${product.slug}`);
    clearSearch();
  }, [router, clearSearch]);

  const handleViewAll = useCallback(() => {
    if (!searchQuery.trim()) return;
    router.push(`/shop/men?search=${encodeURIComponent(searchQuery.trim())}`);
    clearSearch();
  }, [router, searchQuery, clearSearch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleViewAll();
  };

  // ── Logout ─────────────────────────────────────────────────────────────────
  function handleLogout() {
    ["auth_token", "user_name"].forEach(k => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
    setIsLoggedIn(false);
    setUserInitials("US");
    setUserName("");
    setCartCount(0);
    setWishlistCount(0);
    setProfileDrop(false);
    setOpen(false);
    router.push("/login");
  }

  function Badge({ count }: { count: number }) {
    if (count === 0) return null;
    return (
      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-full bg-[#C7AB5F] text-white text-[9px] font-bold flex items-center justify-center leading-none">
        {count > 99 ? "99+" : count}
      </span>
    );
  }

  const showDesktopDrop = desktopFocused && searchQuery.trim().length >= 2;
  const showMobileDrop  = mobileFocused  && searchQuery.trim().length >= 2;

  return (
    <nav className="w-full fixed top-0 left-0 z-50 bg-white border-b border-gray-100 shadow-sm">

      {/* ── MAIN BAR ────────────────────────────────────────────────────────── */}
      <div className="flex w-full items-center justify-between px-4 md:px-10 h-20 md:h-24 gap-3">

        {/* LOGO */}
        <Link href="/" className="shrink-0 transition-transform hover:scale-105">
          <Image src="/mk.png" alt="Logo" width={90} height={70} className="w-[72px] md:w-[110px]" />
        </Link>

        {/* MOBILE: search icon + cart + hamburger */}
        <div className="flex lg:hidden items-center gap-1.5 ml-auto">
          <button
            onClick={() => { setMobileSearchOpen(p => !p); setOpen(false); }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors text-[#051173]"
            aria-label="Toggle search"
          >
            {mobileSearchOpen ? <X size={20} /> : <Search size={20} />}
          </button>

          {isLoggedIn && (
            <Link href="/user/cart" className="relative p-2 rounded-full hover:bg-gray-100 transition-colors text-[#051173]">
              <ShoppingCart size={20} />
              <Badge count={cartCount} />
            </Link>
          )}

          <button className="text-[#051173] z-[60] p-2"
            onClick={() => { setOpen(!open); setMobileSearchOpen(false); }}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* DESKTOP NAV */}
        <ul className="hidden lg:flex items-center gap-7 text-[15px] font-medium text-[#051173]">
          {navLinks.slice(0, 2).map((link) => (
            <li key={link.label}>
              <Link href={link.href}
                className={`transition-colors ${pathname === link.href ? "text-[#C7AB5F] font-semibold" : "text-[#051173] hover:text-[#C7AB5F]"}`}>
                {link.label}
              </Link>
            </li>
          ))}

          {/* Custom Made */}
          <li className="relative py-2 group">
            <button className={`flex items-center gap-1 transition-colors ${pathname.startsWith("/gallery") ? "text-[#C7AB5F]" : "text-[#051173] group-hover:text-[#C7AB5F]"}`}>
              Custom Made Services
              <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <ul className="absolute top-full left-0 w-48 bg-white border border-gray-100 rounded-2xl shadow-xl py-3 mt-1 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out z-50">
              <div className="absolute -top-4 left-0 w-full h-4 bg-transparent" />
              {customMadeServices.map((item) => (
                <li key={item.name}>
                  <Link href={item.href}
                    className={`block px-5 py-2 transition-colors ${pathname === item.href ? "bg-gray-50 text-[#C7AB5F] font-semibold" : "text-[#051173] hover:bg-gray-50 hover:text-[#C7AB5F]"}`}>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </li>

          {/* Shop */}
          <li className="relative py-2 group">
            <button className={`flex items-center gap-1 transition-colors ${pathname.startsWith("/shop") ? "text-[#C7AB5F]" : "text-[#051173] group-hover:text-[#C7AB5F]"}`}>
              Shop
              <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <ul className="absolute top-full left-0 w-40 bg-white border border-gray-100 rounded-2xl shadow-xl py-2.5 mt-1 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out z-50">
              <div className="absolute -top-4 left-0 w-full h-4 bg-transparent" />
              <li>
                <Link href="/shop/men"
                  className={`block px-5 py-2 text-[14px] transition-colors ${pathname === "/shop/men" ? "bg-gray-50 text-[#C7AB5F] font-semibold" : "text-[#051173] hover:bg-gray-50 hover:text-[#C7AB5F]"}`}>
                  Men
                </Link>
              </li>
              <li>
                <Link href="/shop/women"
                  className={`block px-5 py-2 text-[14px] transition-colors ${pathname === "/shop/women" ? "bg-gray-50 text-[#C7AB5F] font-semibold" : "text-[#051173] hover:bg-gray-50 hover:text-[#C7AB5F]"}`}>
                  Women
                </Link>
              </li>
            </ul>
          </li>

          <li>
            <Link href="/career"
              className={`transition-colors ${pathname === "/career" ? "text-[#C7AB5F] font-semibold" : "text-[#051173] hover:text-[#C7AB5F]"}`}>
              Join Us
            </Link>
          </li>

          {navLinks.slice(3).map((link) => (
            <li key={link.label}>
              <Link href={link.href}
                className={`transition-colors ${pathname === link.href ? "text-[#C7AB5F] font-semibold" : "text-[#051173] hover:text-[#C7AB5F]"}`}>
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* DESKTOP SEARCH */}
        <form onSubmit={handleSubmit} ref={desktopSearchRef}
          className="hidden lg:block relative flex-1 max-w-xs">
          <div className={`relative flex items-center rounded-full transition-all ${desktopFocused ? "ring-1 ring-[#C7AB5F]" : ""}`}>
            <input
              type="text"
              placeholder="Search products…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setDesktopFocused(true)}
              onKeyDown={e => { if (e.key === "Escape") { setDesktopFocused(false); setSearchQuery(""); } }}
              className="w-full h-9 pl-4 pr-10 rounded-full border border-gray-200 bg-gray-50 text-sm text-gray-700 outline-none focus:border-[#C7AB5F] focus:bg-white transition-all placeholder:text-gray-400"
            />
            <button type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C7AB5F] transition-colors">
              {(searchLoading && desktopFocused)
                ? <Loader2 size={13} className="text-[#C7AB5F] animate-spin" />
                : <Search size={13} />
              }
            </button>
          </div>

          {showDesktopDrop && (
            <SearchDropdown
              results={searchResults}
              loading={searchLoading}
              query={searchQuery}
              onSelect={handleSelect}
              onViewAll={handleViewAll}
            />
          )}
        </form>

        {/* DESKTOP RIGHT */}
        <div className="hidden lg:flex items-center gap-3">
          <Link href="/form"
            className="px-5 py-2.5 rounded-full bg-[#051173] text-white text-[12px] uppercase tracking-widest font-semibold hover:bg-[#C7AB5F] transition-all whitespace-nowrap">
            Book Appointment
          </Link>

          {isLoggedIn ? (
            <div ref={profileMenuRef} className="relative">
              <button onClick={() => setProfileDrop(!profileDrop)} title={userName || userInitials}
                className="h-10 w-10 rounded-full bg-[#051173] hover:bg-[#C7AB5F] text-white font-mono font-bold text-xs flex items-center justify-center transition-colors border-2 border-white shadow-sm">
                {userInitials}
              </button>

              {profileDrop && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-100 rounded-2xl shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-1">
                  {userName && (
                    <div className="px-4 py-2 border-b border-gray-50 mb-1">
                      <p className="text-[11px] text-gray-400 uppercase tracking-widest">Signed in as</p>
                      <p className="text-[13px] font-semibold text-[#051173] truncate">{userName}</p>
                    </div>
                  )}
                  {[
                    { href: "/user/Profile",     label: "My Profile",      icon: <User size={15} /> },
                    { href: "/user/orders",       label: "Order History",   icon: <ShoppingBag size={15} /> },
                    { href: "/user/appointments", label: "My Appointments", icon: <ShoppingBag size={15} /> },
                    { href: "/user/coupon",       label: "My Coupons",      icon: <Gift size={15} /> },
                  ].map(item => (
                    <Link key={item.href} href={item.href} onClick={() => setProfileDrop(false)}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-[#051173] hover:bg-gray-50 hover:text-[#C7AB5F] transition-colors">
                      {item.icon} {item.label}
                    </Link>
                  ))}
                  <Link href="/user/wishlist" onClick={() => setProfileDrop(false)}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-[#051173] hover:bg-gray-50 hover:text-[#C7AB5F] transition-colors">
                    <Heart size={15} /><span className="flex-1">Wishlist</span>
                    {wishlistCount > 0 && <span className="text-[10px] font-bold bg-[#C7AB5F]/10 text-[#C7AB5F] px-1.5 py-0.5 rounded-full">{wishlistCount}</span>}
                  </Link>
                  <Link href="/user/cart" onClick={() => setProfileDrop(false)}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-[#051173] hover:bg-gray-50 hover:text-[#C7AB5F] transition-colors">
                    <ShoppingCart size={15} /><span className="flex-1">Shopping Cart</span>
                    {cartCount > 0 && <span className="text-[10px] font-bold bg-[#C7AB5F]/10 text-[#C7AB5F] px-1.5 py-0.5 rounded-full">{cartCount}</span>}
                  </Link>
                  <hr className="border-gray-100 my-1.5" />
                  <button onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50/50 transition-colors text-left">
                    <LogOut size={15} /> Logout Account
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link href="/login"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 text-[13px] font-medium text-[#051173] hover:border-[#C7AB5F] hover:text-[#C7AB5F] transition-all">
              <span className="w-6 h-6 rounded-full bg-[#051173] flex items-center justify-center">
                <User size={13} className="text-white" />
              </span>
              Login
            </Link>
          )}
        </div>
      </div>

      {/* ── MOBILE SEARCH BAR ─────────────────────────────────────────────── */}
      {mobileSearchOpen && (
        <div className="lg:hidden px-4 pb-3 bg-white border-b border-gray-100 animate-in slide-in-from-top-1 duration-200">
          <form onSubmit={handleSubmit} ref={mobileSearchRef} className="relative">
            <div className={`relative flex items-center rounded-full transition-all ${mobileFocused ? "ring-1 ring-[#C7AB5F]" : ""}`}>
              <input
                type="text"
                autoFocus
                placeholder="Search products…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setMobileFocused(true)}
                onKeyDown={e => {
                  if (e.key === "Escape") { setMobileSearchOpen(false); setSearchQuery(""); }
                }}
                className="w-full h-10 pl-4 pr-10 rounded-full border border-gray-200 bg-gray-50 text-[13px] text-gray-700 outline-none focus:border-[#C7AB5F] focus:bg-white transition-all placeholder:text-gray-400"
              />
              <button type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C7AB5F] transition-colors">
                {(searchLoading && mobileFocused)
                  ? <Loader2 size={13} className="text-[#C7AB5F] animate-spin" />
                  : <Search size={13} />
                }
              </button>
            </div>

            {showMobileDrop && (
              <SearchDropdown
                results={searchResults}
                loading={searchLoading}
                query={searchQuery}
                onSelect={handleSelect}
                onViewAll={handleViewAll}
              />
            )}
          </form>
        </div>
      )}

      {/* ── MOBILE MENU ───────────────────────────────────────────────────── */}
      {open && (
        <div className="absolute top-[80px] left-0 w-full bg-white shadow-2xl px-5 pb-8 pt-4 lg:hidden flex flex-col gap-0.5 text-[15px] font-semibold text-[#051173] z-50 max-h-[calc(100vh-80px)] overflow-y-auto">

          {navLinks.slice(0, 2).map(link => (
            <Link key={link.label} href={link.href} onClick={() => setOpen(false)}
              className={`py-3.5 border-b border-gray-50 block transition-colors ${pathname === link.href ? "text-[#C7AB5F]" : "text-[#051173]"}`}>
              {link.label}
            </Link>
          ))}

          {/* Custom Made accordion */}
          <div className="flex flex-col border-b border-gray-50">
            <button onClick={() => setMobileCustomOpen(!mobileCustomOpen)}
              className="flex w-full items-center justify-between text-[#051173] py-3.5">
              Custom Made Services
              {mobileCustomOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {mobileCustomOpen && (
              <div className="mb-3 flex flex-col gap-0 pl-2 bg-[#faf7f2] rounded-xl overflow-hidden">
                {[
                  { name: "Men",       href: "/gallery/menT" },
                  { name: "Women",     href: "/gallery/womenT" },
                  { name: "Kids",      href: "/gallery/kidsT" },
                  { name: "Corporate", href: "/gallery/corporateT" },
                ].map(item => (
                  <Link key={item.name} href={item.href} onClick={() => setOpen(false)}
                    className="py-3 px-4 text-[14px] text-[#051173] border-b border-gray-100 last:border-b-0 hover:text-[#C7AB5F] transition-colors">
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Shop accordion — Men / Women */}
          <div className="flex flex-col border-b border-gray-50">
            <button onClick={() => setMobileShopOpen(!mobileShopOpen)}
              className={`flex w-full items-center justify-between py-3.5 transition-colors ${pathname.startsWith("/shop") ? "text-[#C7AB5F]" : "text-[#051173]"}`}>
              <span>Shop</span>
              {mobileShopOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {mobileShopOpen && (
              <div className="mb-3 flex flex-col gap-0 pl-2 bg-[#faf7f2] rounded-xl overflow-hidden">
                <Link href="/shop/men" onClick={() => setOpen(false)}
                  className={`py-3 px-4 text-[14px] border-b border-gray-100 flex items-center gap-2 transition-colors ${pathname === "/shop/men" ? "text-[#C7AB5F] font-semibold" : "text-[#051173] hover:text-[#C7AB5F]"}`}>
                  Men
                </Link>
                <Link href="/shop/women" onClick={() => setOpen(false)}
                  className={`py-3 px-4 text-[14px] flex items-center gap-2 transition-colors ${pathname === "/shop/women" ? "text-[#C7AB5F] font-semibold" : "text-[#051173] hover:text-[#C7AB5F]"}`}>
                  Women
                </Link>
              </div>
            )}
          </div>

          <Link href="/career" onClick={() => setOpen(false)}
            className={`py-3.5 border-b border-gray-50 block transition-colors ${pathname === "/career" ? "text-[#C7AB5F]" : "text-[#051173]"}`}>
            Join Us
          </Link>

          {navLinks.slice(3).map(link => (
            <Link key={link.label} href={link.href} onClick={() => setOpen(false)}
              className={`py-3.5 border-b border-gray-50 block transition-colors ${pathname === link.href ? "text-[#C7AB5F]" : "text-[#051173]"}`}>
              {link.label}
            </Link>
          ))}

          {/* Mobile account */}
          {isLoggedIn && (
            <div className="flex flex-col border-b border-gray-50">
              <button onClick={() => setMobileProfileOpen(!mobileProfileOpen)}
                className="flex w-full items-center justify-between text-[#C7AB5F] font-bold py-3.5">
                {userName ? `Hi, ${userName.split(" ")[0]}` : `Account (${userInitials})`}
                {mobileProfileOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {mobileProfileOpen && (
                <div className="mb-3 flex flex-col gap-0 pl-2 bg-neutral-50 rounded-xl overflow-hidden text-[14px]">
                  {[
                    { href: "/user/Profile",      label: "My Profile"      },
                    { href: "/user/orders",        label: "Order History"   },
                    { href: "/user/appointments",  label: "My Appointments" },
                    { href: "/user/coupon",        label: "My Coupons"      },
                  ].map(item => (
                    <Link key={item.href} href={item.href} onClick={() => setOpen(false)}
                      className="py-3 px-4 text-[#051173] border-b border-gray-100 last:border-b-0 hover:text-[#C7AB5F] transition-colors">
                      {item.label}
                    </Link>
                  ))}
                  <Link href="/user/wishlist" onClick={() => setOpen(false)}
                    className="py-3 px-4 text-[#051173] border-b border-gray-100 flex items-center justify-between hover:text-[#C7AB5F] transition-colors">
                    Wishlist
                    {wishlistCount > 0 && <span className="text-xs font-bold text-[#C7AB5F] bg-[#C7AB5F]/10 px-1.5 py-0.5 rounded-full">{wishlistCount}</span>}
                  </Link>
                  <Link href="/user/cart" onClick={() => setOpen(false)}
                    className="py-3 px-4 text-[#051173] border-b border-gray-100 flex items-center justify-between hover:text-[#C7AB5F] transition-colors">
                    Shopping Cart
                    {cartCount > 0 && <span className="text-xs font-bold text-[#C7AB5F] bg-[#C7AB5F]/10 px-1.5 py-0.5 rounded-full">{cartCount}</span>}
                  </Link>
                  <button onClick={handleLogout}
                    className="py-3 px-4 text-left text-rose-600 font-bold hover:bg-rose-50 transition-colors">
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 mt-5">
            <Link href="/form" onClick={() => setOpen(false)}
              className="w-full text-center py-4 bg-[#051173] text-white rounded-2xl text-[13px] uppercase tracking-widest font-semibold hover:bg-[#C7AB5F] transition-all">
              Book Appointment
            </Link>
            {!isLoggedIn && (
              <Link href="/login" onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border border-gray-200 text-[#051173] font-semibold hover:border-[#C7AB5F] hover:text-[#C7AB5F] transition-colors">
                <User size={16} /> Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}